import { useEffect, useId, useRef } from 'react';
import './jqueryGlobals';
import $ from 'jquery';
// eslint-disable-next-line @typescript-eslint/no-explicit-any
import select2Factory from 'select2';
(select2Factory as unknown as (w: Window, jq: typeof $) => void)(window, $);

import type { Select2Option } from './Select2';

interface Select2MultiProps {
  label?: string;
  value: (string | number)[];
  onChange: (value: string[]) => void;
  options: Select2Option[];
  placeholder?: string;
  searchable?: boolean;
  disabled?: boolean;
  error?: string;
  id?: string;
  className?: string;
  // Render the dropdown into this container (outside any overflow-scroll region, e.g. a
  // modal body or a page wrapper above the table scroll) so it isn't clipped. Inline by default.
  dropdownParent?: HTMLElement | null;
}

// Multi-select variant of Select2 (real select2 in `multiple` mode). Value is an
// array of selected values; onChange returns the current selection as strings.
export function Select2Multi({
  label,
  value,
  onChange,
  options,
  placeholder = 'All',
  searchable = true,
  disabled = false,
  error,
  id,
  className,
  dropdownParent,
}: Select2MultiProps) {
  const autoId = useId();
  const fieldId = id ?? autoId;
  const selectRef = useRef<HTMLSelectElement>(null);
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  const optionsKey = options.map((o) => `${o.value}:${o.label}`).join('|');
  const valueKey = value.map(String).join(',');

  useEffect(() => {
    const el = selectRef.current;
    if (!el) return;
    const $el = $(el);

    const opts: Record<string, unknown> = {
      width: '100%',
      placeholder,
      multiple: true,
      minimumResultsForSearch: searchable ? 0 : Infinity,
    };
    if (dropdownParent) opts.dropdownParent = $(dropdownParent);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    ($el as any).select2(opts);
    $el.val(value.map(String)).trigger('change.select2');

    const onSel = () => {
      const v = ($el.val() as string[] | null) ?? [];
      onChangeRef.current(v.map(String));
    };
    $el.on('change', onSel);

    return () => {
      $el.off('change', onSel);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      if (($el as any).data('select2')) ($el as any).select2('destroy');
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [optionsKey, placeholder, searchable, disabled, dropdownParent]);

  useEffect(() => {
    const el = selectRef.current;
    if (!el) return;
    const $el = $(el);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    if (($el as any).data('select2')) {
      $el.val(value.map(String)).trigger('change.select2');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [valueKey]);

  return (
    <div className={className || undefined}>
      {label && (
        <label htmlFor={fieldId} className="form-label">
          {label}
        </label>
      )}
      <select id={fieldId} ref={selectRef} className={`form-control${error ? ' is-invalid' : ''}`} multiple disabled={disabled}>
        {options.map((o) => (
          <option key={o.value} value={String(o.value)} disabled={o.disabled}>
            {o.label}
          </option>
        ))}
      </select>
      {error && <div className="invalid-feedback d-block">{error}</div>}
    </div>
  );
}
